if (!customElements.get('delivery-estimate')) {
  /**
   * Shows the shipping cut-off sentence and the delivery window as a date range,
   * e.g. "Lieferung voraussichtlich zwischen Di., 21.10. und Do., 23.10." (KT-WOR-5551).
   * Calculated in the browser from the current shop time (Europe/Berlin) so cached
   * pages stay current. Orders before the cut-off on a working day ship today and
   * show the cut-off sentence; later orders ship on the next working day and the
   * sentence is hidden. Saturdays and Sundays are skipped; public holidays are not
   * considered.
   */
  class DeliveryEstimate extends HTMLElement {
    static SHOP_TIME_ZONE = 'Europe/Berlin';

    connectedCallback() {
      const cutoffHour = parseInt(this.dataset.cutoffHour, 10);
      const minDays = parseInt(this.dataset.minDays, 10);
      const maxDays = parseInt(this.dataset.maxDays, 10);
      const cutoff = this.querySelector('.product-stock__cutoff');
      const delivery = this.querySelector('.product-stock__delivery');
      if (Number.isNaN(cutoffHour)) return;

      const now = DeliveryEstimate.shopNow();
      const shipsToday = DeliveryEstimate.isWorkingDay(now.date) && now.hour < cutoffHour;
      const shippingDate = shipsToday ? now.date : DeliveryEstimate.addWorkingDays(now.date, 1);

      if (cutoff) cutoff.hidden = !shipsToday;

      const template = delivery?.dataset.template;
      if (delivery && template && !Number.isNaN(minDays) && !Number.isNaN(maxDays)) {
        const format = (date) => DeliveryEstimate.formatDate(date, this.dataset.locale);
        delivery.textContent = template
          .replace('[start]', format(DeliveryEstimate.addWorkingDays(shippingDate, minDays)))
          .replace('[end]', format(DeliveryEstimate.addWorkingDays(shippingDate, maxDays)));
        delivery.hidden = false;
      }

      this.hidden = (!cutoff || cutoff.hidden) && (!delivery || delivery.hidden);
    }

    /**
     * Returns the current calendar date and hour in the shop time zone.
     * The date is a UTC midnight Date, so day arithmetic is free of DST shifts.
     * @returns {{ date: Date, hour: number }}
     */
    static shopNow() {
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-US', {
          timeZone: DeliveryEstimate.SHOP_TIME_ZONE,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          hourCycle: 'h23',
        })
          .formatToParts(new Date())
          .map(({ type, value }) => [type, parseInt(value, 10)])
      );
      return {
        date: new Date(Date.UTC(parts.year, parts.month - 1, parts.day)),
        hour: parts.hour,
      };
    }

    /**
     * @param {Date} date - UTC midnight Date.
     * @returns {boolean} True for Monday to Friday.
     */
    static isWorkingDay(date) {
      const weekday = date.getUTCDay();
      return weekday !== 0 && weekday !== 6;
    }

    /**
     * Returns the date that lies the given number of working days after the start date.
     * @param {Date} start - UTC midnight Date.
     * @param {number} days - Working days to add.
     * @returns {Date}
     */
    static addWorkingDays(start, days) {
      const date = new Date(start);
      let added = 0;
      while (added < days) {
        date.setUTCDate(date.getUTCDate() + 1);
        if (DeliveryEstimate.isWorkingDay(date)) added += 1;
      }
      return date;
    }

    /**
     * Formats a date as short weekday plus day and month, e.g. "Mo., 27.10." for de-DE.
     * @param {Date} date - UTC midnight Date.
     * @param {string} locale - BCP 47 locale, e.g. "de-DE".
     * @returns {string}
     */
    static formatDate(date, locale) {
      const options = { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'UTC' };
      try {
        return date.toLocaleDateString(locale, options);
      } catch (error) {
        return date.toLocaleDateString(undefined, options);
      }
    }
  }

  customElements.define('delivery-estimate', DeliveryEstimate);
}
